import {
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  ImageOff,
  Link2,
  Loader2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';

import { ParticlesBackground } from '@/components/layout/ParticlesBackground';

import { downloadAsset, type AssetResponse } from '../../api';
import { useFileDownload } from '../../useFileDownload';
import { HintTooltip } from '../HintTooltip';
import { useAssetLibraryPermissions } from '../../useAssetLibraryPermissions';
import { buildAssetPermalink } from '../../assetPermalink';
import {
  formatDateDDMMYYYY,
  getAssetExpiryDate,
  isAssetExpired,
  isAssetExpiringSoon,
} from '../../constants/taxonomy';
import { mediaKindOf, rulesOf, specOf } from '../../constants/assetTypes';
import { AssetMetadataForm, type MetadataFormHandle } from '../grid/AssetMetadataForm';
import { useAssetPublish } from '../grid/useAssetPublish';
import { sourceImageBadge } from '../grid/sourceImageBadge';
import { SourceImageThumbs } from '../grid/SourceImageThumbs';
import type { AssetUrlLookup } from '../grid/useAssetUrls';
import type { OlaTokenLookup } from '../grid/useOlaTokens';
import { CarouselFramesEditor, type FrameEditRequest } from './CarouselFramesEditor';
import { CarouselStage } from './CarouselStage';
import { CopyCaption } from './CopyCaption';
import { OlaStage } from './OlaStage';
import { VideoPlayer } from './VideoPlayer';

/**
 * The one place asset metadata is edited — for one asset or for fifty.
 *
 * A full-screen overlay rather than a column beside the grid, and that is the
 * whole point of it. The grid can afford exactly one side column before the
 * page becomes three competing columns with the thumbnails crushed between
 * them, and the Published tab is getting a panel of its own. Taking over the
 * viewport means metadata editing never shares a screen with the grid, so the
 * column stays free whatever Published puts in it.
 *
 * One asset and many are the same view with a different canvas: a full-size
 * preview you can step through, or a contact sheet of the selection. The rail
 * is identical either way, because it is the same form — bulk editing is just
 * the case where the selection has more than one member, and giving it its own
 * panel is what let the two drift apart in the first place.
 *
 * The rail is also where per-asset panels go from here (usage, source image
 * family, whatever Published needs): appended as another section, not parked
 * beside the grid.
 */

interface AssetDetailOverlayProps {
  /**
   * What is being edited. One asset gets the full preview and stepping; more
   * than one gets the contact sheet. Never empty — the caller doesn't render
   * this without a selection.
   */
  assets: AssetResponse[];
  /** Signed URLs for everything on screen, owned by the caller. */
  urls: AssetUrlLookup;
  /** Capability tokens for the OLA banners on screen, owned by the caller. */
  olaTokens: OlaTokenLookup;
  /**
   * The list behind the overlay, in display order, for stepping between single
   * assets. The open asset need not be in it — a permalink can name one this
   * page never loaded — in which case stepping is simply unavailable.
   */
  siblings: AssetResponse[];
  onNavigate: (assetId: string) => void;
  onClose: () => void;
  /**
   * Narrow the selection behind the overlay to these ids.
   *
   * Only used after a partial publish, to keep the editor open on the rows the
   * backend refused. Absent for the single-asset case, which has no selection
   * to narrow.
   */
  onNarrowSelection?: (assetIds: string[]) => void;
}

/** Same budget, and same reasoning, as the grid card: one re-sign per failure. */
const MAX_URL_RETRIES = 1;

/**
 * The contact sheet confirms what a bulk edit is about to touch; it is not a
 * second grid. Past this many tiles it stops answering that question and just
 * costs scrolling, so the rest become a count.
 */
const MAX_CONTACT_SHEET_TILES = 60;

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Arrow keys belong to the caret whenever one is in play.
 *
 * Without this, pressing Left in the middle of a nameplate steps to the
 * previous asset and throws the half-typed value away — the form resets on
 * selection change — which is data loss wearing the costume of a shortcut.
 *
 * The multi-select pickers are buttons, not a `<select>`, so they are matched
 * by role: the trigger and every option inside the open listbox.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable === true ||
    target.closest('[role="listbox"], [aria-haspopup="listbox"]') !== null
  );
}

/** What the carousel stage and frames editor share for the asset on show. */
interface CarouselView {
  assetId: string;
  current: number;
  openId: string | null;
  request: FrameEditRequest | null;
}

const freshView = (assetId: string): CarouselView => ({
  assetId,
  current: 0,
  openId: null,
  request: null,
});

export function AssetDetailOverlay({
  assets,
  urls,
  olaTokens,
  siblings,
  onNavigate,
  onClose,
  onNarrowSelection,
}: AssetDetailOverlayProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  // Held so closing the overlay mid-tick doesn't leave a setState pointed at an
  // unmounted component, and so a second copy doesn't get cut short by the
  // first one's timer.
  const copyTimeout = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(copyTimeout.current), []);

  // Asset Library's own permissions. The backend refuses the same writes per
  // row, so read-only here only stops the form offering a save that would be
  // skipped.
  const { permissions } = useAssetLibraryPermissions();
  const isReadOnly =
    !permissions.can_manage_assets ||
    (assets.some((a) => a.status === 'published') && !permissions.can_edit_published);
  // The bulk view's wording follows the same rule as the form: a read-only
  // user who clicked "Open" must not land on "Editing 5 assets".
  const bulkVerb = isReadOnly ? 'Viewing' : 'Editing';

  const isSingle = assets.length === 1;
  const asset = assets[0];
  const isExpired = isSingle && isAssetExpired(asset);
  const isExpiringSoon = isSingle && !isExpired && isAssetExpiringSoon(asset);
  // Single assets only: a selection downloads as a zip from the toolbar.
  const canDownloadThis =
    isSingle && asset.asset_type === 'source_image' && asset.status === 'published';
  const fileDownload = useFileDownload();
  // Of the asset on screen, not of the overlay: see `useFileDownload`.
  const downloadStatus = fileDownload.statusOf(asset.id);

  // Every source this asset was cut from, in stacking order. `SourceImageThumbs`
  // has always taken an array and caps the stack itself, so a composite of five
  // needs nothing here beyond handing the list over.
  const parentSourceImageIds = useMemo(
    () => (isSingle ? (asset.source_image_ids ?? []) : []),
    [isSingle, asset],
  );
  const parentSourceImageCodes = useMemo(() => {
    const ids = isSingle ? (asset.source_image_ids ?? []) : [];
    const codes = isSingle ? (asset.source_image_codes ?? []) : [];
    // Only paired when every source has a code. `source_image_codes` omits
    // sources whose code has not been allocated yet, so the lists can differ in
    // length and zipping them then would label each thumbnail with the *next*
    // source's Image ID — a wrong code is worse than no code. The thumbnails
    // still render either way.
    if (ids.length === 0 || ids.length !== codes.length) return undefined;
    return new Map(ids.map((id, index) => [id, codes[index]]));
  }, [isSingle, asset]);
  const sourceBadge = sourceImageBadge(isSingle ? asset.source_image_codes : []);

  // Publishing from inside the overlay, because the overlay is where a bulk
  // edit happens and it covers the viewport: the selection toolbar's Publish
  // button is not just off-screen there, it is unreachable without closing the
  // edit, going back to the grid and finding the selection again.
  //
  // `staged` rather than "not published": the grid is queried one status at a
  // time, so a selection is never mixed, but a single asset can arrive here by
  // permalink from any status at all — and only a staged one has somewhere to
  // go.
  const formHandle = useRef<MetadataFormHandle | null>(null);
  const canPublish = assets[0].status === 'staged' && permissions.can_manage_assets;
  const publish = useAssetPublish(assets, {
    onPublished: onClose,
    // Stay open on exactly the rows that failed, rather than closing and
    // leaving the user to find them again in the grid. They are already the
    // selection, so the rail reloads onto them.
    onPartiallyPublished: (skippedIds) => onNarrowSelection?.(skippedIds),
  });

  // Saving first, rather than disabling Publish while the rail is dirty. The
  // fields being edited are usually the very ones that make the assets
  // publishable, so "fill in the nameplate, Save, then Publish" is the normal
  // path and a button that goes grey at exactly that moment reads as broken.
  // The publish is abandoned if the save doesn't land cleanly — publishing
  // rows whose edits were rejected is worse than not publishing at all.
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // A carousel frame with an unsaved draft or a save in flight. Frames save
  // on their own, so Publish waits for them rather than publishing past them
  // (and closing the overlay on the draft) or saving them unannounced.
  const [framesBusy, setFramesBusy] = useState(false);

  // A carousel's stage and its frames editor are one surface: the frame on
  // show is the row marked in the editor, opening a row brings its frame up,
  // and the caption's "Add copy" / "Edit" opens that frame's row. Held here,
  // where both live, and reset whenever the overlay moves to another asset.
  const carouselFrames = asset.frames ?? [];
  const [carouselView, setCarouselView] = useState<CarouselView>(() => freshView(asset.id));
  const view = carouselView.assetId === asset.id ? carouselView : freshView(asset.id);
  const updateView = (patch: Partial<CarouselView>) => setCarouselView({ ...view, ...patch });
  const positionOf = (frameId: string) =>
    Math.max(
      0,
      carouselFrames.findIndex((candidate) => candidate.id === frameId),
    );
  const openFrame = (frameId: string | null) =>
    updateView(frameId ? { openId: frameId, current: positionOf(frameId) } : { openId: null });
  const editFrame = (frameId: string) =>
    updateView({
      openId: frameId,
      current: positionOf(frameId),
      request: { frameId, nonce: (view.request?.nonce ?? 0) + 1 },
    });
  const handlePublish = async () => {
    if (hasUnsavedChanges && formHandle.current) {
      setIsSaving(true);
      const saved = await formHandle.current.save();
      setIsSaving(false);
      if (!saved) return;
    }
    await publish.publishAsync();
  };
  const expiryDate = isSingle ? getAssetExpiryDate(asset) : null;

  // The URL that failed, not a boolean: a freshly signed URL is a different
  // string, so the preview un-breaks itself without an effect to reset it.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const retries = useRef(0);
  const url = urls.urlById.get(asset.id);
  const isBroken = url !== undefined && url === failedUrl;

  const index = isSingle ? siblings.findIndex((candidate) => candidate.id === asset.id) : -1;
  const previous = index > 0 ? siblings[index - 1] : null;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null;

  // Focus moves into the dialog on open so Escape and the arrows work without a
  // click first, and so screen readers announce the overlay rather than leaving
  // the user on the card underneath.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // The page behind must not scroll under the overlay: a wheel gesture over the
  // preview otherwise moves the grid, and closing lands somewhere else entirely.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (isTypingTarget(event.target)) return;
      if (event.key === 'ArrowLeft' && previous) {
        event.preventDefault();
        onNavigate(previous.id);
      }
      if (event.key === 'ArrowRight' && next) {
        event.preventDefault();
        onNavigate(next.id);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previous, next, onNavigate, onClose]);

  // A new asset gets its own retry budget and its own broken flag; without the
  // reset, stepping past one dead object would leave the next one showing the
  // fallback even though its URL is fine.
  useEffect(() => {
    retries.current = 0;
    setFailedUrl(null);
  }, [asset.id]);

  const handleCopyLink = useCallback(async () => {
    const link = buildAssetPermalink(asset.id, window.location.href);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied');
      // The tick is the confirmation people actually look at; the toast is for
      // the case where the button has already scrolled out of view.
      window.clearTimeout(copyTimeout.current);
      copyTimeout.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused outright in some embedded browsers, and a
      // silent no-op there looks exactly like a successful copy — until the
      // paste comes out empty.
      toast.error('Could not copy the link');
    }
  }, [asset.id]);

  const handleImageError = () => {
    if (url === undefined) return;
    setFailedUrl(url);
    if (retries.current >= MAX_URL_RETRIES) return;
    retries.current += 1;
    urls.refresh();
  };

  const visibleTiles = assets.slice(0, MAX_CONTACT_SHEET_TILES);
  const hiddenTileCount = assets.length - visibleTiles.length;

  // Portalled to `body` because `fixed inset-0` is not enough on its own here.
  // The page renders inside `<main class="relative z-[1]">`, which is a
  // stacking context, so every z-index below is resolved *within* it — the
  // overlay covered the viewport correctly and still had the app's sidebar
  // painted on top of it, because the sidebar is a sibling of that `main`. No
  // z-index on this component can win that; leaving the context is the fix.
  return createPortal(
    <>
      {/* Opaque, and the same slate as the canvas: the dialog covers this
          entirely, so its only job is to make sure nothing of the grid shows
          through in the frame before the canvas paints. */}
      <div className="fixed inset-0 z-[90] bg-slate-100" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={
          isSingle ? `Asset ${asset.filename_original}` : `${bulkVerb} ${assets.length} assets`
        }
        tabIndex={-1}
        className="fixed inset-0 z-[100] flex flex-col outline-none"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
          {isSingle ? (
            <>
              {/* An asset cut from source images has no code of its own, so it
                  shows theirs as its Image ID — outlined rather than filled, so
                  the solid pill keeps meaning "this is the hero". */}
              {asset.source_image_code ? (
                <span className="shrink-0 rounded bg-[#0014cc]/10 px-1.5 py-0.5 text-[11px] font-medium text-[#0014cc]">
                  {asset.source_image_code}
                </span>
              ) : (
                parentSourceImageIds.length > 0 && (
                  <>
                    {sourceBadge && (
                      <span
                        title={sourceBadge.title}
                        className="shrink-0 rounded border border-[#0014cc]/20 px-1.5 py-0.5 text-[11px] font-medium text-[#0014cc]/70"
                      >
                        {sourceBadge.label}
                      </span>
                    )}
                    {/* The pictures beside the codes they belong to, all of
                        them — this is the detail view, and it is where "what is
                        this made of?" gets a complete answer rather than a
                        count. `SourceImageThumbs` caps the stack itself.
                        Rendered on the ids, not the codes: a source whose code
                        has not been allocated yet still has a thumbnail, and
                        dropping it here would hide a real link behind a missing
                        label.
                        Signs itself, because the source image of the asset on
                        screen is frequently not on the page the grid signed — a
                        staged banner cut from a published hero is the common
                        case. */}
                    {/* Roles that cannot see source images keep the code
                        badge above but get no pictures: the backend will not
                        sign a URL for a source image they cannot open. */}
                    {permissions.can_view_source_images && (
                      <SourceImageThumbs
                        sourceImageIds={parentSourceImageIds}
                        codes={parentSourceImageCodes}
                      />
                    )}
                  </>
                )
              )}
              <h2
                className="min-w-0 flex-1 truncate text-[13px] font-bold text-slate-800"
                title={asset.filename_original}
              >
                {asset.filename_original}
              </h2>

              {/* Only on a single asset: a permalink names one asset, and there
                  is no honest link to "these twelve I happened to select". */}
              <button
                type="button"
                onClick={() => void handleCopyLink()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-800"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                ) : (
                  <Link2 className="h-3.5 w-3.5" strokeWidth={2} />
                )}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </>
          ) : (
            <h2 className="min-w-0 flex-1 text-[13px] font-bold text-slate-800">
              {bulkVerb} {assets.length} assets
            </h2>
          )}

          {/* Hidden, not disabled, for roles without the permission: a client
              never takes files out of the library, and a greyed button would
              only make them wonder what they are missing. Only published
              source images download at all, so other kinds get no button.
              Expired ones do get it, greyed with the reason, which is the same
              signal the Published tab gives (CAT-2351). The server enforces
              every one of these rules; this only avoids offering a refusal. */}
          {permissions.can_download_assets && canDownloadThis && (
            <HintTooltip
              hint={
                isExpired
                  ? `Rights expired on ${formatDateDDMMYYYY(getAssetExpiryDate(asset))}. Expired assets cannot be downloaded.`
                  : undefined
              }
              // Above the overlay, which sits at z-[100].
              contentClassName="z-[110]"
            >
              <button
                type="button"
                disabled={isExpired || downloadStatus.isDownloading}
                aria-busy={downloadStatus.isDownloading}
                onClick={() =>
                  void fileDownload.download(asset.id, (onProgress) =>
                    downloadAsset(asset.id, onProgress),
                  )
                }
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-400"
              >
                {downloadStatus.isDownloading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                ) : (
                  <Download className="h-3.5 w-3.5" strokeWidth={2} />
                )}
                {/* A percentage when the size is known, which for one file it is;
                  otherwise the spinner alone says it is still working. */}
                {downloadStatus.isDownloading
                  ? downloadStatus.progress === null
                    ? 'Downloading…'
                    : `Downloading ${Math.round(downloadStatus.progress * 100)}%`
                  : 'Download'}
              </button>
            </HintTooltip>
          )}

          {canPublish && (
            <button
              type="button"
              // Eligibility is only checked when there is nothing unsaved. A
              // dirty rail is very likely the fix for whatever makes these rows
              // ineligible, and greying the button out then would leave no way
              // to act on the edit that was just typed.
              disabled={
                (!hasUnsavedChanges && publish.publishable === 0) ||
                isSaving ||
                publish.isPending ||
                framesBusy
              }
              onClick={() => void handlePublish()}
              title={
                framesBusy
                  ? 'Save or cancel your frame edits first'
                  : hasUnsavedChanges
                    ? 'Saves your changes, then publishes'
                    : publish.title
              }
              className="shrink-0 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              {isSaving
                ? 'Saving…'
                : publish.isPending
                  ? 'Publishing…'
                  : hasUnsavedChanges
                    ? // Named for what the click actually does. A plain
                      // "Publish" that silently wrote the form first would be
                      // an unannounced save.
                      'Save & publish'
                    : isSingle
                      ? 'Publish'
                      : 'Publish all'}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label={isSingle ? 'Close asset' : isReadOnly ? 'Close' : 'Close bulk edit'}
            className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* `flex-[3]` here and `flex-[2]` on the rail so the stacked layout
              below `lg` splits the height 60/40, instead of letting the rail's
              natural height take everything and leave the canvas a letterbox.
              Above `lg` the rail is a fixed width and this means "take the
              rest".

              Same slate-100 and the same drifting particles as the rest of the
              app, rather than a photographer's dark room. The overlay is a view
              of the library, not a separate product, and the dark canvas made
              it feel like one. Creatives keep their edges against it through a
              drop shadow instead of through contrast with the backdrop. */}
          <div
            className="relative flex min-h-0 flex-[3] items-center justify-center overflow-hidden bg-slate-100 p-6"
            onClick={(event) => {
              // Only the canvas itself, so a click that lands on the image or
              // on an arrow doesn't close the overlay out from under it.
              if (event.target === event.currentTarget) onClose();
            }}
          >
            {/* Absolute, and its own id: the shell's instance is still mounted
                underneath, and tsparticles keys its containers by id. */}
            <ParticlesBackground id="asset-overlay-particles" position="absolute" />

            {isSingle ? (
              <>
                {specOf(asset).hasFrames ? (
                  // Every frame, not just the first: the grid's single URL is
                  // frame 1, which is all a card has room for.
                  // Keyed by asset: a new carousel starts at frame 1 with its
                  // deck measured fresh, not with the last one's index.
                  <CarouselStage
                    key={asset.id}
                    asset={asset}
                    current={view.current}
                    onCurrentChange={(current) => updateView({ current })}
                    onEditFrame={isReadOnly ? undefined : editFrame}
                  />
                ) : asset.asset_type === 'ola' ? (
                  // Owns the whole stage: an OLA is played, replayed and
                  // diagnosed here, and none of that fits the img/video shape
                  // the other two branches share.
                  <OlaStage
                    asset={asset}
                    token={olaTokens.tokenById.get(asset.id)}
                    posterUrl={isBroken ? undefined : url}
                  />
                ) : url && !isBroken ? (
                  mediaKindOf(asset.asset_type) === 'video' ? (
                    <VideoPlayer
                      src={url}
                      title={asset.filename_original}
                      onError={handleImageError}
                    />
                  ) : rulesOf(asset) === 'master_image' ? (
                    // A master image carries copy, so it gets the same card a
                    // carousel frame does: its source and its copy, labelled,
                    // and the way in to editing that copy from where it shows.
                    <div className="relative flex h-full w-full flex-col items-center gap-4 pb-10">
                      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center">
                        <img
                          src={url}
                          alt={asset.filename_original}
                          onError={handleImageError}
                          onLoad={() => {
                            retries.current = 0;
                          }}
                          // The shadow is what separates the creative from the canvas
                          // now that both can be light. `relative` lifts it clear of
                          // the particle canvas behind it.
                          className="relative max-h-full max-w-full rounded object-contain shadow-[0_12px_40px_-8px_rgb(15_23_42/0.35)]"
                        />
                      </div>
                      <CopyCaption
                        sourceCodes={asset.source_image_codes ?? []}
                        filename={asset.filename_original}
                        copy={asset.copy_elements}
                        subject="image"
                        onEdit={isReadOnly ? undefined : () => formHandle.current?.focusCopy()}
                      />
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={asset.filename_original}
                      onError={handleImageError}
                      onLoad={() => {
                        retries.current = 0;
                      }}
                      // The shadow is what separates the creative from the canvas
                      // now that both can be light. `relative` lifts it clear of
                      // the particle canvas behind it.
                      className="relative max-h-full max-w-full rounded object-contain shadow-[0_12px_40px_-8px_rgb(15_23_42/0.35)]"
                    />
                  )
                ) : urls.isLoading && !isBroken ? (
                  <div className="relative h-3/4 w-3/4 animate-pulse rounded bg-slate-200" />
                ) : (
                  <div className="relative flex flex-col items-center gap-2 text-slate-400">
                    <ImageOff className="h-8 w-8" strokeWidth={1.5} />
                    <span className="text-[12.5px]">Preview unavailable</span>
                  </div>
                )}

                {previous && (
                  <button
                    type="button"
                    onClick={() => onNavigate(previous.id)}
                    aria-label="Previous asset"
                    className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-500 shadow-sm transition-colors hover:bg-white hover:text-slate-800"
                  >
                    <ChevronLeft className="h-5 w-5" strokeWidth={2} />
                  </button>
                )}
                {next && (
                  <button
                    type="button"
                    onClick={() => onNavigate(next.id)}
                    aria-label="Next asset"
                    className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-500 shadow-sm transition-colors hover:bg-white hover:text-slate-800"
                  >
                    <ChevronRight className="h-5 w-5" strokeWidth={2} />
                  </button>
                )}

                {index >= 0 && (
                  <span className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full border border-slate-200 bg-white/90 px-2.5 py-1 text-[11px] font-medium text-slate-500 shadow-sm">
                    {index + 1} of {siblings.length}
                  </span>
                )}
              </>
            ) : (
              // The contact sheet. It answers one question — "what am I about
              // to change?" — so the tiles are deliberately inert: a click
              // target here would either alter the selection mid-edit or drop
              // the bulk context, and both are worse than no click target.
              //
              // `self-start` pins the sheet to the top of the canvas: the
              // parent centres its contents for the single-asset preview, and
              // inheriting that floated the tiles in the middle of the canvas
              // where their reading order stopped being obvious. Top-left and
              // in order is how the grid behind reads, and this is the same
              // assets. `max-h-full` keeps a long selection scrolling inside
              // the canvas instead of pushing past it.
              <div className="relative flex max-h-full w-full flex-col gap-3 self-start overflow-y-auto">
                {/* Wrapping flex of fixed-width tiles, not an auto-fill grid: a
                    grid stretches its tracks to fill the row, so two selected
                    assets would become two enormous tiles and twelve would
                    become small ones. A fixed tile is the same size whatever
                    the selection, and left-aligned wrapping reads in the same
                    order as the grid these came from. */}
                <div className="flex flex-wrap gap-3">
                  {visibleTiles.map((tile) => {
                    // The thumbnail where there is one: a contact sheet of
                    // originals is the grid's 200 MB problem all over again.
                    const tileThumbnail = urls.thumbnailById.get(tile.id);
                    const tileUrl = tileThumbnail ?? urls.urlById.get(tile.id);
                    return (
                      <figure
                        key={tile.id}
                        className="flex w-48 flex-col gap-1 overflow-hidden rounded-lg border border-slate-200 bg-white p-2 shadow-sm"
                      >
                        <div className="flex h-44 items-center justify-center">
                          {tileUrl ? (
                            mediaKindOf(tile.asset_type) === 'video' && !tileThumbnail ? (
                              <video
                                src={tileUrl}
                                className="max-h-full max-w-full object-contain"
                                preload="metadata"
                                muted
                              />
                            ) : (
                              <img
                                src={tileUrl}
                                alt={tile.filename_original}
                                loading="lazy"
                                className="max-h-full max-w-full object-contain"
                              />
                            )
                          ) : (
                            <ImageOff className="h-4 w-4 text-slate-300" strokeWidth={1.5} />
                          )}
                        </div>
                        {/* The filename earns its line here in a way it doesn't
                            on a 96px square: without it a contact sheet of
                            near-identical resizes is unreadable. */}
                        <figcaption
                          className="truncate px-0.5 text-[11px] text-slate-500"
                          title={tile.filename_original}
                        >
                          {tile.filename_original}
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
                {hiddenTileCount > 0 && (
                  <p className="text-center text-[11.5px] text-slate-500">
                    and {hiddenTileCount} more
                    {!isReadOnly && ` — all ${assets.length} will be saved`}
                  </p>
                )}
              </div>
            )}
          </div>

          <aside
            aria-label="Asset metadata"
            className="flex min-h-0 w-full flex-[2] flex-col gap-4 overflow-y-auto border-t border-slate-200 bg-white p-4 lg:w-[360px] lg:flex-none lg:border-l lg:border-t-0"
          >
            {isSingle ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  File
                </h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
                  <dt className="text-slate-500">Status</dt>
                  <dd className="capitalize text-slate-800">{asset.status}</dd>
                  {expiryDate && (
                    <>
                      <dt className="text-slate-500">Expiry rights</dt>
                      {/* DD/MM/YYYY by spec; the editable field below keeps the
                          native date input and its ISO value. */}
                      <dd className="flex items-center gap-1.5 text-slate-800">
                        <span>{formatDateDDMMYYYY(expiryDate)}</span>
                        {isExpired && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                            Expired
                          </span>
                        )}
                        {isExpiringSoon && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                            Expires soon
                          </span>
                        )}
                      </dd>
                    </>
                  )}
                  <dt className="text-slate-500">Uploaded</dt>
                  <dd className="text-slate-800">{formatDate(asset.created_at)}</dd>
                  <dt className="text-slate-500">By</dt>
                  <dd className="truncate text-slate-800" title={asset.uploaded_by}>
                    {asset.uploaded_by}
                  </dd>
                </dl>
              </section>
            ) : (
              <p className="text-[12px] text-slate-500">
                {isReadOnly
                  ? 'Fields the selection disagrees on show “Mixed”.'
                  : 'Fields the selection disagrees on show “Mixed” and stay untouched until you change them.'}
              </p>
            )}

            {/* A carousel's frames carry their own source image and copy, so
                they are edited here, beside the carousel's own fields. */}
            {isSingle && specOf(asset).hasFrames && (
              <section className="flex flex-col gap-2 border-t border-slate-200 pt-3">
                <h3 className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Frames
                </h3>
                <CarouselFramesEditor
                  key={asset.id}
                  asset={asset}
                  readOnly={isReadOnly}
                  activeFrameId={carouselFrames[view.current]?.id}
                  openFrameId={view.openId}
                  onOpenFrameChange={openFrame}
                  editRequest={view.request}
                  onBusyChange={setFramesBusy}
                />
              </section>
            )}

            <section className="flex flex-col gap-2 border-t border-slate-200 pt-3">
              <h3 className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Metadata
              </h3>
              {/* The same form either way — that is the point of the unified
                  overlay. Editing one asset is the single-member case. */}
              <AssetMetadataForm
                selectedAssets={assets}
                readOnly={isReadOnly}
                handleRef={formHandle}
                onDirtyChange={setHasUnsavedChanges}
              />
            </section>
          </aside>
        </div>
      </div>
    </>,
    document.body,
  );
}
